import { toBig } from '@talos/kyoko';
import Big from 'big.js';

export const REPO_MARGIN_PERCENT_DISPLAY_SCALE = 2;

export type RepoMarginThresholdMode = 'initial' | 'release' | 'maintenance' | 'marginCall' | 'closeOut';

/** Formats a repo margin threshold decimal (e.g. 1.1111) as a percentage string (e.g. "111.11"). */
export function formatRepoMarginPercentDecimal(decimalValue: string | undefined): string | undefined {
  if (!decimalValue) {
    return undefined;
  }
  const decimalBig = toBig(decimalValue);
  if (decimalBig == null) {
    return undefined;
  }
  return decimalBig.times(100).toFixed(REPO_MARGIN_PERCENT_DISPLAY_SCALE);
}

/**
 * Computes repo margin threshold as a decimal (e.g. 1.111 for 111.1%).
 * Formulas from GMRA buffers + haircut:
 * - Initial: 1 / (1 − HaircutPct)
 * - Release: (1 / (1 − HaircutPct)) × (1 + ReleaseBufferPct)
 * - Maintenance/MarginCall/CloseOut: (1 / (1 − HaircutPct)) × (1 − BufferPct)
 */
export function computeRepoMarginThresholdDecimal(
  haircutDecimal: string | undefined,
  bufferDecimal: string | undefined,
  mode: RepoMarginThresholdMode
): string | undefined {
  const haircut = toBig(haircutDecimal);
  if (haircut == null) {
    return undefined;
  }
  // If no buffer was set up for this threshold, don't compute a default — a missing buffer
  // isn't the same as a zero buffer
  if (bufferDecimal == null && mode !== 'initial') {
    return undefined;
  }
  const buffer = toBig(bufferDecimal) ?? new Big(0);
  const one = new Big(1);
  const denominator = one.minus(haircut);
  if (denominator.lte(0)) {
    return undefined;
  }

  let multiplier;
  switch (mode) {
    case 'initial':
      multiplier = one;
      break;
    case 'release':
      multiplier = one.plus(buffer);
      break;
    case 'maintenance':
    case 'marginCall':
    case 'closeOut':
      multiplier = one.minus(buffer);
      break;
  }

  if (multiplier == null || multiplier.lte(0)) {
    return undefined;
  }

  return multiplier.div(denominator).toFixed();
}

export interface GMRADefaultsFromGMRA {
  haircutPct: string | undefined;
  initialMarginPct: string | undefined;
  marginReleasePct: string | undefined;
  maintenanceMarginPct: string | undefined;
  marginCallPct: string | undefined;
  closeOutMarginPct: string | undefined;
}

export function computeGMRADefaultThresholds(
  haircutPct: string | undefined,
  releaseBufferPct: string | undefined,
  maintenanceBufferPct: string | undefined,
  marginCallBufferPct: string | undefined,
  closeOutBufferPct: string | undefined
): GMRADefaultsFromGMRA {
  return {
    haircutPct,
    initialMarginPct: computeRepoMarginThresholdDecimal(haircutPct, undefined, 'initial'),
    marginReleasePct: computeRepoMarginThresholdDecimal(haircutPct, releaseBufferPct, 'release'),
    maintenanceMarginPct: computeRepoMarginThresholdDecimal(haircutPct, maintenanceBufferPct, 'maintenance'),
    marginCallPct: computeRepoMarginThresholdDecimal(haircutPct, marginCallBufferPct, 'marginCall'),
    closeOutMarginPct: computeRepoMarginThresholdDecimal(haircutPct, closeOutBufferPct, 'closeOut'),
  };
}

export function computeGMRADisplayThresholds(
  haircutPct: string | undefined,
  releaseBufferPct: string | undefined,
  maintenanceBufferPct: string | undefined,
  marginCallBufferPct: string | undefined,
  closeOutBufferPct: string | undefined
): GMRADefaultsFromGMRA {
  return computeGMRADefaultThresholds(
    haircutPct,
    releaseBufferPct ?? '0',
    maintenanceBufferPct ?? '0',
    marginCallBufferPct ?? '0',
    closeOutBufferPct ?? '0'
  );
}
